import React, { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { Input as AntInput, Modal, Select as AntSelect } from "antd";
import { IMaster, MasterType } from "../../../types/api/master";
import { removeSpecialChars } from "../../../lib/utils";

export interface MasterFormValues {
  name: string;
  slug: string;
  type: MasterType | "";
  status: boolean;
}

const MASTER_TYPE_OPTIONS: { label: string; value: MasterType }[] = [
  { label: "Subject", value: "SUBJECT" },
  { label: "Style", value: "STYLE" },
  { label: "Medium", value: "MEDIUM" },
  { label: "Material", value: "MATERIAL" },
  { label: "Finish", value: "FINISH" },
  { label: "Method", value: "METHOD" },
  { label: "Base", value: "BASE" },
  { label: "Tags", value: "TAGS" },
];

const emptyValues: MasterFormValues = {
  name: "",
  slug: "",
  type: "",
  status: true,
};

interface MasterFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: MasterFormValues) => void;
  submitting?: boolean;
  editingMaster?: IMaster | null;
  defaultType?: MasterType | "";
}

export const MasterFormModal: React.FC<MasterFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  submitting,
  editingMaster,
  defaultType = "",
}) => {
  const { control, handleSubmit, reset, setValue, watch } =
    useForm<MasterFormValues>({ defaultValues: emptyValues });

  const slugManuallyEdited = useRef(false);

  useEffect(() => {
    if (!open) return;
    slugManuallyEdited.current = !!editingMaster?.slug;

    if (editingMaster) {
      reset({
        name: editingMaster.name ?? "",
        slug: editingMaster.slug ?? "",
        type: editingMaster.type ?? "",
        status: editingMaster.status ?? true,
      });
    } else {
      reset({
        ...emptyValues,
        type: defaultType || "",
      });
    }
  }, [open, editingMaster, defaultType, reset]);

  const nameValue = watch("name");

  useEffect(() => {
    if (!slugManuallyEdited.current) {
      setValue("slug", removeSpecialChars(nameValue || ""));
    }
  }, [nameValue, setValue]);

  const isTypeLocked = !!editingMaster || !!defaultType;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      title={editingMaster ? "Edit Master" : "Add Master"}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        {/* Name */}
        <Controller
          name="name"
          control={control}
          rules={{ required: "Please enter a name" }}
          render={({ field, fieldState }) => (
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <AntInput
                {...field}
                placeholder="e.g. Abstract"
                status={fieldState.error ? "error" : ""}
              />
              {fieldState.error && (
                <p className="text-xs text-red-500 mt-1">
                  {fieldState.error.message}
                </p>
              )}
            </div>
          )}
        />

        {/* Slug — auto-generated from name */}
        <Controller
          name="slug"
          control={control}
          rules={{ required: "Slug is required" }}
          render={({ field, fieldState }) => (
            <div>
              <label className="block text-sm font-medium mb-1">Slug</label>
              <AntInput
                {...field}
                placeholder="auto-generated-from-name"
                status={fieldState.error ? "error" : ""}
                onChange={(e) => {
                  slugManuallyEdited.current = true;
                  field.onChange(removeSpecialChars(e.target.value));
                }}
              />
              {fieldState.error && (
                <p className="text-xs text-red-500 mt-1">
                  {fieldState.error.message}
                </p>
              )}
            </div>
          )}
        />

        {/* Type */}
        <Controller
          name="type"
          control={control}
          rules={{ required: "Please select a type" }}
          render={({ field, fieldState }) => (
            <div>
              <label className="block text-sm font-medium mb-1">Type</label>
              <AntSelect
                value={field.value || undefined}
                onChange={field.onChange}
                options={MASTER_TYPE_OPTIONS}
                placeholder="Select type"
                className="w-full"
                disabled={isTypeLocked}
                status={fieldState.error ? "error" : ""}
              />
              {fieldState.error && (
                <p className="text-xs text-red-500 mt-1">
                  {fieldState.error.message}
                </p>
              )}
            </div>
          )}
        />

        {/* Status */}
        <Controller
          name="status"
          control={control}
          render={({ field }) => (
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <AntSelect
                value={field.value}
                onChange={field.onChange}
                options={[
                  { label: "Active", value: true },
                  { label: "Inactive", value: false },
                ]}
                className="w-full"
              />
            </div>
          )}
        />

        <div className="flex justify-end gap-2 mt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-1.5 rounded-md bg-primary text-white disabled:opacity-60"
          >
            {editingMaster ? "Update" : "Create"}
          </button>
        </div>
      </form>
    </Modal>
  );
};
